import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Input } from 'antd';
import {
  SearchOutlined,
  TrophyOutlined,
  CheckCircleOutlined,
  FireOutlined,
  RiseOutlined,
} from '@ant-design/icons';
import { selectors, fetchDevPlan, fetchDevPlans } from 'state';

import type { CategoryId, Difficulty, Game } from './games';
import { CATEGORIES, PROFILE } from './games';
import GameCard, { isPlayable } from './components/GameCard';
import GamePlayView from './components/GamePlayView';
import AssetPlayerModal from '../DevelopmentPlan/components/AssetPlayerModal';
import { unlockedGameKeys } from '../DevelopmentPlan/helpers';
import useDevPassAccess from '../DevelopmentPlan/useDevPassAccess';
import type { DevAsset, DevPlanState } from '../DevelopmentPlan/types';
import { latestVisiblePlan, linkedBuiltGames } from './selectors';
import './puzzles.css';

type CatFilter = CategoryId | 'all';

const Puzzles: React.FC = () => {
  const dispatch = useDispatch();
  const username = useSelector(selectors.username);
  const selectedPerson = useSelector(selectors.selectedPerson);
  const plans = useSelector(selectors.devPlans);
  const plansFetched = useSelector(selectors.devPlansFetched);
  const devPlan = useSelector(selectors.devPlan) as DevPlanState | undefined;
  const devPlanFetched = useSelector(selectors.devPlanFetched);
  const itemPlanCode = useSelector(selectors.devPlanItemCode);
  const latestPlan = useMemo(() => latestVisiblePlan(plans), [plans]);
  const latestPlanCode = latestPlan?.code;
  const firstName = String(selectedPerson?.name || username || 'Trader').split(
    ' ',
  )[0];

  const [cat, setCat] = useState<CatFilter>('all');
  const [query, setQuery] = useState('');
  const [playing, setPlaying] = useState<Game | null>(null);
  const [openAsset, setOpenAsset] = useState<DevAsset | null>(null);
  const [requestedPlans, setRequestedPlans] = useState(false);

  // Open-play mirror: progression mode is no longer a toggle — `?devPass=1` selects
  // open play for the Development Plan tab, and this tab reads the same flag so the
  // two agree. In open play every game is unlocked here too, the same lifted-locks
  // rule the node panel applies.
  const openPlay = useDevPassAccess();

  // Games always follow the newest plan the trainee can see, across plan families.
  // /plans is already restricted to published, non-hidden trees; selecting by database
  // id makes this independent of whether the trainee visited the Development Plan tab.
  useEffect(() => {
    dispatch(fetchDevPlans({}));
    setRequestedPlans(true);
  }, [dispatch]);

  useEffect(() => {
    if (requestedPlans && plansFetched && latestPlanCode) {
      dispatch(fetchDevPlan({ planCode: latestPlanCode }));
    }
  }, [dispatch, requestedPlans, plansFetched, latestPlanCode]);

  // Accept the /me payload only when it is the successfully loaded newest visible plan.
  // This prevents a hidden/failed plan request from leaving an older Redux item visible.
  const visiblePlan =
    plansFetched &&
    devPlanFetched &&
    latestPlanCode &&
    itemPlanCode === latestPlanCode
      ? devPlan
      : undefined;

  // Real Development-Plan HTML games surfaced as catalog cards. The /me assets pool
  // is global, so membership is explicitly constrained by this plan's node_games.
  // Each linked, built game is grayed until any node serving it unlocks — the same
  // shared rule (unlockedGameKeys) that gates games in the DevPlan panel.
  // Card presentation (category/difficulty/blurb/skills/icon) is seeded on the
  // asset and served in /me; when absent (seed not yet run) the card falls back
  // to a bare filename + beaker, so BE/FE need no lockstep deploy.
  const realGames: Game[] = useMemo(() => {
    if (!visiblePlan) return [];
    const unlocked = unlockedGameKeys(visiblePlan, !openPlay);
    return linkedBuiltGames(visiblePlan).map(
      (a): Game => ({
        id: `dp:${a.key}`,
        title: a.title, // filename for v1 (D2 — humanized titles deferred)
        blurb: a.blurb || '',
        category: (a.category || undefined) as CategoryId | undefined,
        difficulty: (a.difficulty || undefined) as Difficulty | undefined,
        minutes: a.minutes ?? undefined,
        skills: a.skills || undefined,
        icon: a.icon || 'experiment',
        status: unlocked.has(a.key) ? 'new' : 'locked',
        backing: 'devplan',
        assetKey: a.key,
        filePath: a.file_path,
      }),
    );
  }, [visiblePlan, openPlay]);

  const assetByKey = useMemo(() => {
    const m: Record<string, DevAsset> = {};
    visiblePlan?.assets.forEach(a => {
      m[a.key] = a;
    });
    return m;
  }, [visiblePlan]);

  // Only games linked to the newest visible Development Plan belong in this grid.
  const allGames = realGames;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const rows = allGames.filter(g => {
      // Seeded categories place games under the 6 pills; a game with no category
      // appears only under "All".
      if (cat !== 'all' && g.category !== cat) return false;
      if (
        q &&
        !`${g.title} ${g.blurb} ${g.skills ?? ''}`.toLowerCase().includes(q)
      )
        return false;
      return true;
    });
    // Playable cards first so the unlocked games rise above the grayed cards;
    // preserve source order within each group.
    return rows
      .map((g, i) => ({ g, i }))
      .sort((a, b) => {
        const pa = isPlayable(a.g) ? 0 : 1;
        const pb = isPlayable(b.g) ? 0 : 1;
        return pa - pb || a.i - b.i;
      })
      .map(x => x.g);
  }, [allGames, cat, query]);

  const countFor = (id: CatFilter) =>
    id === 'all'
      ? allGames.length
      : allGames.filter(g => g.category === id).length;

  const launch = (g: Game) => {
    if (g.embed) {
      setPlaying(g);
      return;
    }
    if (g.backing === 'devplan' && g.status !== 'locked' && g.assetKey) {
      const asset = assetByKey[g.assetKey];
      if (asset) setOpenAsset(asset);
      return;
    }
    // Grayed / inert (mock cards with no game, or still-locked devplan games):
    // no action — the card is a visual placeholder only.
  };

  const catPills: { id: CatFilter; label: string; color: string }[] = [
    { id: 'all', label: 'All games', color: '#0f5fd6' },
    ...CATEGORIES.map(c => ({
      id: c.id as CatFilter,
      label: c.label,
      color: c.color,
    })),
  ];

  // Hero stat cards (Rating / Solved / Day streak / Cohort rank) are still fed
  // by mock PROFILE data (see games.ts) — hidden until wired to real per-trainee
  // stats. Flip SHOW_STATS to true once the backend fields exist.
  const SHOW_STATS = false;
  const stats = [
    {
      icon: <TrophyOutlined />,
      label: 'Rating',
      value: PROFILE.rating.toLocaleString(),
    },
    {
      icon: <CheckCircleOutlined />,
      label: 'Solved',
      value: `${PROFILE.solved}/${PROFILE.total}`,
    },
    { icon: <FireOutlined />, label: 'Day streak', value: `${PROFILE.streak}` },
    { icon: <RiseOutlined />, label: 'Cohort rank', value: PROFILE.percentile },
  ];

  if (playing) {
    return (
      <div className="puzzles">
        <GamePlayView game={playing} onExit={() => setPlaying(null)} />
      </div>
    );
  }

  return (
    <div className="puzzles">
      {/* ---- Welcome hero ---- */}
      <header className="puzzles__hero">
        <div className="puzzles__hero-main">
          <span className="puzzles__eyebrow">
            Rokos Capital · Assessment Lab
          </span>
          <h1 className="puzzles__title">Games</h1>
          <p className="puzzles__tagline">
            Welcome back, {firstName}. A macro trading gym — timed challenges
            that pressure-test the instincts the desk runs on: rates,
            cross-asset reasoning, conviction, and risk under pain.
          </p>
        </div>
        {SHOW_STATS && (
          <div className="puzzles__hero-stats">
            {stats.map(s => (
              <div className="puzzles__stat" key={s.label}>
                <span className="puzzles__stat-icon">{s.icon}</span>
                <span className="puzzles__stat-value">{s.value}</span>
                <span className="puzzles__stat-label">{s.label}</span>
              </div>
            ))}
          </div>
        )}
      </header>

      {/* ---- Filter toolbar ---- */}
      <div className="puzzles__toolbar">
        <div className="puzzles__cats">
          {catPills.map(p => {
            const active = cat === p.id;
            return (
              <button
                key={p.id}
                type="button"
                className={`puzzles__cat-pill${active ? ' is-active' : ''}`}
                style={
                  active
                    ? {
                        borderColor: p.color,
                        color: p.color,
                        background: `${p.color}14`,
                      }
                    : undefined
                }
                onClick={() => setCat(p.id)}
              >
                {p.label}
                <span className="puzzles__cat-count">{countFor(p.id)}</span>
              </button>
            );
          })}
        </div>
        <Input
          allowClear
          className="puzzles__search"
          prefix={<SearchOutlined />}
          placeholder="Search games, skills…"
          value={query}
          onChange={e => setQuery(e.target.value)}
        />
      </div>

      {/* ---- Game-select grid ---- */}
      <section className="puzzles__grid">
        {filtered.map(g => (
          <GameCard key={g.id} game={g} onLaunch={launch} />
        ))}
      </section>

      {filtered.length === 0 && (
        <div className="puzzles__empty">No games match your filters.</div>
      )}

      <AssetPlayerModal asset={openAsset} onClose={() => setOpenAsset(null)} />
    </div>
  );
};

export default Puzzles;
